import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import AppFormatResourceStatusLabel from "../../../../../../../../../../../../components/FormatStatus";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	CompanyApi,
	InvoiceApi,
	InvoiceDetailsApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import InvoiceForm from "../../../../../Invoice/components/InvoiceForm/InvoiceForm";
import { OrderContext } from "../../../../pages/Preview";

interface MatchParams {
	invoiceId: string;
}

const OrganizationOrderOrdersInvoiceEditPage = () => {
	const [loading, setLoading] = useState(false);
	const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsApi | undefined>();
	const [companyInfo, setCompanyInfo] = useState<CompanyApi | undefined>();
	const { orderInvoiceCustomFieldsConfig, order, fetchOrderInvoices } = useContext(OrderContext);
	const { invoiceId } = useParams<MatchParams>();
	const [invoice, setInvoice] = useState<InvoiceApi | undefined>();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const confirmation = useConfirmation();
	const history = useHistory();

	useEffect(() => {
		init();
	}, [invoiceId]);

	const init = async () => {
		await fetchInvoice();
		await fetchInvoiceDetails();
		await fetchCompanyInfo();
	};

	const fetchInvoiceDetails = async () => {
		try {
			const res = await apiOrganization.getInvoiceDetails();
			setInvoiceDetails(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchCompanyInfo = async () => {
		try {
			const res = await apiOrganization.getCompany({ include: "all" });
			setCompanyInfo(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const getInvoiceTitle = (invoice: InvoiceApi) => {
		return invoice.status === "DRAFT"
			? `${t("enums.invoices.document_type.PROFORMA")} #${invoice.id}`
			: `${t("enums.invoices.document_type.INVOICE")} #${invoice.document_number.full_number}`;
	};

	const fetchInvoice = async () => {
		setLoading(true);
		try {
			const res = await apiOrganization.getInvoice(Number(invoiceId), { include: "all" });
			setInvoice(res);
			const title = `${order.document_number} | ${t("modules.order.header.title")} | ${t(
				"modules.order.field.invoices.title"
			)} | ${getInvoiceTitle(res)}`;
			handleChangeTabTitle(title);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	if (!invoice || !orderInvoiceCustomFieldsConfig || !invoiceDetails || !companyInfo || loading)
		return <LoadingContainer />;

	const handleRemove = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});
			await apiOrganization.removeInvoice(Number(invoiceId));
			await fetchOrderInvoices();
			addSuccessFlash(t("lib:common.flash.removed"));
			history.replace(`/${organization.id}/documents/sales/${order.id}/invoices/new`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const renderTitle = () => {
		return (
			<>
				{getInvoiceTitle(invoice)}
				<AppFormatResourceStatusLabel
					status={invoice.status}
					customStatuses={[{ status: "CLOSED", label: "success" }]}
				/>
			</>
		);
	};

	const buttons: ButtonProps[] = [
		{
			title: t("modules.order.action.full_edit.title"),
			path: `/${organization.id}/documents/invoices/${invoice.id}`,
			variant: "primary",
		},
		{
			title: t("lib:common.action.remove"),
			dropdown: true,
			action: handleRemove,
		},
	];

	return (
		<div>
			<Header title={renderTitle()} buttons={buttons} />
			<InvoiceForm
				invoice={invoice}
				invoiceDetails={invoiceDetails}
				customFieldsConfig={orderInvoiceCustomFieldsConfig}
			/>
		</div>
	);
};

export default OrganizationOrderOrdersInvoiceEditPage;
